import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';
import axios, { type AxiosError } from 'axios';

import { AUTH_ERROR_STORAGE_KEY } from '@/constants/common';
import {
  globalNavigate,
  REDIRECT_STORAGE_KEY,
  TOKEN_STORAGE_KEY
} from '@/hooks/Auth.hooks';
import type { components } from '@/types/openapi';

export const axiosClient = axios.create({
  baseURL: '/api'
});

// General API error handler for use query. Redirects to logout page on 401 and 412 errors
export const handleApiError = (error: unknown): null => {
  if (!axios.isAxiosError(error) || !error.response) {
    console.error('Unknown API Error', error);
    return null;
  }

  const { status, data } = error.response;

  switch (status) {
    case 403:
    case 401:
      sessionStorage.setItem(
        AUTH_ERROR_STORAGE_KEY,
        data?.detail || 'Not authorized'
      );
      break;

    case 412:
      sessionStorage.setItem(
        AUTH_ERROR_STORAGE_KEY,
        `${data?.detail} Please click the link in the confirmation email to gain access`
      );
      break;

    default:
      console.error('API Error:', error);
      break;
  }

  if ([401, 403, 412].includes(status)) {
    sessionStorage.setItem(
      REDIRECT_STORAGE_KEY,
      window.location.pathname + window.location.search
    );

    // Resolves circular dependency
    globalNavigate.navigate('/logout');
  }

  // must return null because react-query expects a return value
  return null;
};

// global axios interceptor to add the token to the request headers
axiosClient.interceptors.request.use((config) => {
  const newConfig = config;
  newConfig.headers.Authorization = `Bearer ${localStorage.getItem(
    TOKEN_STORAGE_KEY
  )}`;
  return config;
});

// global axios interceptor to handle API response errors
axios.interceptors.response.use((response) => {
  return response;
}, handleApiError);

const defaultQueryFn = async ({
  queryKey
}: {
  queryKey: readonly unknown[] | [string];
}) => {
  const { data } = await axiosClient.get(`/${queryKey[0]}`);
  return data;
};

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: defaultQueryFn,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      retry: (failureCount, error) => {
        // retry on 502 and 503 errors up to 2 times
        if (
          [502, 503].includes((error as AxiosError).response?.status || 0) &&
          failureCount < 2
        ) {
          return true;
        }
        return false;
      },
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 15000) // delay the retry but cap it at 15 seconds
    }
  },
  queryCache: new QueryCache({
    onError: handleApiError
  }),
  mutationCache: new MutationCache({
    onError: handleApiError
  })
});

export default queryClient;

// utility function to get error messages from the response and parse the pydantic validation errors
export const getResponseErrorMessages = (error: AxiosError) => {
  const errorMessages = error.response
    ?.data as components['schemas']['HTTPValidationError'];

  let messages = '';
  if (Array.isArray(errorMessages?.detail)) {
    messages = errorMessages?.detail
      ?.map((detail) => {
        const field = detail.loc[1] || '';
        return `${field ? `${field.toString().toUpperCase()}: ` : ''} ${
          detail.msg
        }`;
      })
      .join(', ');
  } else {
    messages = errorMessages?.detail || 'Error executing operation';
  }

  return messages;
};
